import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getFile, getFileUrl } from 'entities/Files/api';
import { getChangingText, retryImportFile } from 'entities/ImportSessions/api';
import { PageWrapper } from 'pages/_layouts/PageWrapper';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import FileViewer from 'components/file-editor-components/file-viewer';
import EditorWrapper from 'components/file-editor-components/editor-wrapper';
import LabelFields from 'components/file-editor-components/label-fields';
import TableFields from 'components/file-editor-components/table-fields';
import FieldLabelModal from 'components/file-editor-components/field-label-modal';
import { useFileEditor } from 'components/file-editor-components/useFileEditor';
import FileInfo from 'components/file-editor-components/file-info';

const GeneralFileEditor = () => {
	const { fileId } = useParams();
	const navigate = useNavigate();

	const {
		fileDetails,
		fileUrl,
		isLoading,
		fileViewerRef,
		pendingRegion,
		setPendingRegion,
		drawMode,
		setDrawMode,
		pendingCellTarget,
		pendingLabelTarget,
		regions,
		setRegions,
		selectedSourceId,
		setSelectedSourceId,
		updateLabelContent,
		handleCancelLabel,
		handleSaveLabel,
		handleCellTargetClick,
		handleLabelTargetClick,
		handleCellContentChange,
		handleCellCandidateSelect,
		handleCellDelete,
		handleRegionCreated,
		handleRegionUpdated,
		handleAddRow,
		handleDeleteRow,
		handleReorderColumns,
		getColumnPinPreferences,
		handleTogglePinColumn,
	} = useFileEditor({
		entityId: fileId,
		detailsQueryKey: ['get/files_v2', fileId],
		urlQueryKey: ['get/fileUrl_v2', fileId],
		regionTextQueryKey: 'get/rectangle-content/cell',
		getDetails: getFile,
		getUrl: getFileUrl,
		getRegionText: getChangingText,
	});

	const { mutate: saveFile, isLoading: isSaving } = useMutation({
		mutationKey: ['patch/files/recognition-result'],
		mutationFn: retryImportFile,
	});

	const handleSave = () => {
		if (!fileDetails) return;
		saveFile(
			{
				id: fileDetails._id,
				data: {
					recognizedLabels: fileDetails.recognizedLabels || [],
					assertionSetsId: fileDetails.assertionSetsId || null,
					partnerId: fileDetails.partnerId || null,
					receiverId: fileDetails.receiverId,
					isPreview: false,
					autocomplete: fileDetails.autocomplete,
				},
			},
			{
				onSuccess: () => {
					navigate(-1);
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	return (
		<PageWrapper>
			<EditorWrapper
				onSave={handleSave}
				isSaving={isSaving}
				isLoading={isLoading}
				filePreview={
					fileUrl && (
						<FileViewer
							ref={fileViewerRef}
							url={fileUrl}
							fileName={fileDetails?.fileName || ''}
							regions={regions}
							onRegionsChange={setRegions}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							onRegionNeedsLabel={setPendingRegion}
							onRegionCreated={handleRegionCreated}
							onRegionUpdated={handleRegionUpdated}
							onDrawModeChange={setDrawMode}
						/>
					)
				}
				sidebar={
					<>
						{fileDetails && <FileInfo file={fileDetails} />}
						<LabelFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							onContentChange={updateLabelContent}
							drawMode={drawMode}
							pendingLabelTarget={pendingLabelTarget}
							onLabelTargetClick={handleLabelTargetClick}
						/>
						<TableFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							drawMode={drawMode}
							pendingCellTarget={pendingCellTarget}
							onCellTargetClick={handleCellTargetClick}
							onCellContentChange={handleCellContentChange}
							onCellCandidateSelect={handleCellCandidateSelect}
							onAddRow={handleAddRow}
							onDeleteRow={handleDeleteRow}
							onReorderColumns={handleReorderColumns}
							getColumnPinPreferences={getColumnPinPreferences}
							onTogglePinColumn={handleTogglePinColumn}
						/>
					</>
				}
			/>
			<FieldLabelModal
				fileId={fileId}
				region={pendingRegion}
				onCancel={handleCancelLabel}
				onSave={handleSaveLabel}
			/>
		</PageWrapper>
	);
};

export default GeneralFileEditor;
